import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  attendancePersonalistaBankAdjust,
  attendancePersonalistaBankDetail,
  attendancePersonalistaBankNormalize,
  attendancePersonalistaBankOverview,
  type AttendanceBankOverviewRow,
  type AttendanceBankaEntry,
} from '../../lib/apiPhpAdapter'
import { formatEmployeeFullName } from '../../utils/employeeDisplay'
import { comparePersonNames } from '../../utils/personNameSort'
import './AttendanceOrgAdmin.css'

function bankRowDisplayName(r: AttendanceBankOverviewRow): string {
  return formatEmployeeFullName({
    name: r.employee_name,
    first_name: r.first_name,
    last_name: r.last_name,
    title: r.title,
    title_after: r.title_after,
  })
}

function bankRowPersonFields(r: AttendanceBankOverviewRow) {
  return {
    name: r.employee_name,
    first_name: r.first_name,
    last_name: r.last_name,
  }
}

type Props = {
  year: number
  month: number
  busy: boolean
  setBusy: (v: boolean) => void
  showAlert: (msg: string, type?: 'success' | 'error' | 'warning' | 'info') => void
}

type SignFilter = 'all' | 'plus' | 'minus'
type HoursSort = 'hours_desc' | 'hours_asc' | 'name'

export function AttendanceBankaPanel({ year, month, busy, setBusy, showAlert }: Props) {
  const [search, setSearch] = useState('')
  const [signFilter, setSignFilter] = useState<SignFilter>('all')
  const [hoursSort, setHoursSort] = useState<HoursSort>('hours_desc')
  const [rows, setRows] = useState<AttendanceBankOverviewRow[]>([])
  const [loading, setLoading] = useState(true)
  const [selected, setSelected] = useState<Set<number>>(() => new Set())
  const [bankDialog, setBankDialog] = useState<{
    employeeId: number
    name: string
    balanceMinutes: number
    balanceLabel: string
    target: string
    note: string
    entries: AttendanceBankaEntry[]
    loading: boolean
  } | null>(null)

  const periodLabel = `${String(month).padStart(2, '0')}/${year}`

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const res = await attendancePersonalistaBankOverview(year, month)
      setRows(Array.isArray(res.employees) ? res.employees : [])
      setSelected(new Set())
    } catch (e: unknown) {
      showAlert(e instanceof Error ? e.message : 'Banku se nepodařilo načíst', 'error')
    } finally {
      setLoading(false)
    }
  }, [year, month, showAlert])

  useEffect(() => {
    void reload()
  }, [reload])

  /** Zůstatek k vyrovnání = sloupec „Z minulého“ (= ledger). */
  const carryMinutes = (r: AttendanceBankOverviewRow) => r.opening_minutes

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase()
    let list = rows
    if (signFilter === 'plus') {
      list = list.filter((r) => carryMinutes(r) > 0)
    } else if (signFilter === 'minus') {
      list = list.filter((r) => carryMinutes(r) < 0)
    }
    if (q) {
      list = list.filter((r) => {
        const name = bankRowDisplayName(r).toLowerCase()
        const pn = (r.personal_number || '').toLowerCase()
        return name.includes(q) || pn.includes(q)
      })
    }
    const sorted = [...list]
    sorted.sort((a, b) => {
      if (hoursSort === 'name') {
        return comparePersonNames(bankRowPersonFields(a), bankRowPersonFields(b))
      }
      const da = carryMinutes(a)
      const db = carryMinutes(b)
      if (da !== db) return hoursSort === 'hours_desc' ? db - da : da - db
      return comparePersonNames(bankRowPersonFields(a), bankRowPersonFields(b))
    })
    return sorted
  }, [rows, search, signFilter, hoursSort])

  const overtimeCount = useMemo(
    () => filtered.filter((r) => carryMinutes(r) > 0).length,
    [filtered],
  )
  const undertimeCount = useMemo(
    () => filtered.filter((r) => carryMinutes(r) < 0).length,
    [filtered],
  )

  const toggleHoursSort = () => {
    setHoursSort((prev) => (prev === 'hours_desc' ? 'hours_asc' : 'hours_desc'))
  }

  const selectedRows = useMemo(
    () => filtered.filter((r) => selected.has(r.employee_id)),
    [filtered, selected],
  )

  const allFilteredSelected =
    filtered.length > 0 && filtered.every((r) => selected.has(r.employee_id))

  const toggleAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (allFilteredSelected) {
        for (const r of filtered) next.delete(r.employee_id)
      } else {
        for (const r of filtered) next.add(r.employee_id)
      }
      return next
    })
  }

  const toggleOne = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const openBankDialog = (r: AttendanceBankOverviewRow) => {
    const name = bankRowDisplayName(r) || '—'
    setBankDialog({
      employeeId: r.employee_id,
      name,
      balanceMinutes: r.current_balance_minutes,
      balanceLabel: r.current_balance_label,
      target: String(r.current_balance_minutes),
      note: '',
      entries: [],
      loading: true,
    })
    void attendancePersonalistaBankDetail(r.employee_id)
      .then((d) => {
        setBankDialog((prev) =>
          prev && prev.employeeId === r.employee_id
            ? {
                ...prev,
                balanceMinutes: d.balance_minutes,
                balanceLabel: d.balance_label,
                target: String(d.balance_minutes),
                entries: Array.isArray(d.entries) ? d.entries : [],
                loading: false,
              }
            : prev,
        )
      })
      .catch((e: unknown) => {
        setBankDialog((prev) =>
          prev && prev.employeeId === r.employee_id ? { ...prev, loading: false } : prev,
        )
        showAlert(e instanceof Error ? e.message : 'Historie banky se nepodařila načíst', 'error')
      })
  }

  const saveBankDialog = (forceZero = false) => {
    if (!bankDialog) return
    const target = forceZero ? 0 : parseInt(String(bankDialog.target).trim(), 10)
    if (!Number.isFinite(target)) {
      showAlert('Zadejte cílový zůstatek v minutách (např. 90 nebo -30).', 'info')
      return
    }
    if (target === bankDialog.balanceMinutes) {
      showAlert('Zůstatek banky se nezměnil.', 'info')
      return
    }
    void (async () => {
      setBusy(true)
      try {
        const res = await attendancePersonalistaBankAdjust({
          employee_id: bankDialog.employeeId,
          balance_minutes: target,
          note:
            bankDialog.note.trim() ||
            (forceZero || target === 0 ? 'Vyrovnání zůstatku na 0' : undefined),
        })
        showAlert(`Banka upravena → ${res.banka.balance_label}`, 'success')
        setBankDialog(null)
        await reload()
      } catch (e: unknown) {
        showAlert(e instanceof Error ? e.message : 'Úprava banky selhala', 'error')
      } finally {
        setBusy(false)
      }
    })()
  }

  const runNormalize = (side: 'overtime' | 'undertime') => {
    const pool = selected.size > 0 ? selectedRows : filtered
    const targets = pool.filter((r) =>
      side === 'overtime' ? carryMinutes(r) > 0 : carryMinutes(r) < 0,
    )
    if (targets.length === 0) {
      showAlert(
        side === 'overtime'
          ? 'Nikdo ve výběru nemá kladné „Z minulého“ (+).'
          : 'Nikdo ve výběru nemá záporné „Z minulého“ (−).',
        'info',
      )
      return
    }
    const scope =
      selected.size > 0
        ? `u ${targets.length} označených`
        : `u všech ${targets.length} ve filtru`
    const label = side === 'overtime' ? 'kladná „Z minulého“ (+)' : 'záporná „Z minulého“ (−)'
    if (
      !window.confirm(
        `Vyrovnat ${label} na 0 ${scope}?\n\nSloupec „Z minulého“ (zůstatek banky) se nastaví na 0. Tuto akci nelze vrátit jedním klikem.`,
      )
    ) {
      return
    }
    void (async () => {
      setBusy(true)
      try {
        const res = await attendancePersonalistaBankNormalize({
          normalize: side,
          employee_ids: targets.map((r) => r.employee_id),
          note:
            side === 'overtime'
              ? 'Vyrovnání Z minulého (+) na 0'
              : 'Vyrovnání Z minulého (−) na 0',
        })
        showAlert(
          side === 'overtime'
            ? `Vyrovnáno Z minulého (+): ${res.count}`
            : `Vyrovnáno Z minulého (−): ${res.count}`,
          'success',
        )
        await reload()
      } catch (e: unknown) {
        showAlert(e instanceof Error ? e.message : 'Vyrovnání selhalo', 'error')
      } finally {
        setBusy(false)
      }
    })()
  }

  const entryTypeLabel = (t: string) => {
    if (t === 'period_close') return 'Uzávěrka'
    if (t === 'period_reopen') return 'Odemčení'
    if (t === 'manual') return 'Ruční'
    return t || '—'
  }

  return (
    <div className="att-cat att-bank-panel">
      <p className="att-cat__intro att-bank-no-print">
        Banka hodin za {periodLabel}. <strong>Z minulého</strong> = zůstatek banky — Vyrovnat + / −
        nastaví tento sloupec na 0 (kladné a záporné zvlášť). Tento měsíc = pohyb z uzávěrky, pokud
        je měsíc zaúčtován.
      </p>

      <div className="att-leave-form__filters att-bank-toolbar att-bank-no-print">
        <label className="att-leave-form__search">
          Hledat
          <input
            type="search"
            value={search}
            placeholder="jméno / os. číslo"
            onChange={(e) => setSearch(e.target.value)}
          />
        </label>
        <label>
          Zůstatek
          <select
            value={signFilter}
            onChange={(e) => setSignFilter(e.target.value as SignFilter)}
          >
            <option value="all">Vše</option>
            <option value="plus">Jen + (přepracování)</option>
            <option value="minus">Jen − (nedopracování)</option>
          </select>
        </label>
        <label>
          Řazení
          <select value={hoursSort} onChange={(e) => setHoursSort(e.target.value as HoursSort)}>
            <option value="hours_desc">Hodiny ↓</option>
            <option value="hours_asc">Hodiny ↑</option>
            <option value="name">Jméno</option>
          </select>
        </label>
        <span className="attendance-muted">
          {filtered.length}/{rows.length}
          {selected.size > 0 ? ` · označeno ${selected.size}` : ''}
          {` · +${overtimeCount} / −${undertimeCount}`}
        </span>
        <div className="att-bank-toolbar__actions">
          <button
            type="button"
            className="system-button att-cat__btn"
            disabled={busy || loading}
            onClick={() => window.print()}
          >
            Tisk
          </button>
          <button
            type="button"
            className="system-button att-cat__btn"
            disabled={busy || loading || overtimeCount === 0}
            title="Nastaví kladné „Z minulého“ na 0 (označené, jinak filtr)"
            onClick={() => runNormalize('overtime')}
          >
            Vyrovnat +
          </button>
          <button
            type="button"
            className="system-button att-cat__btn"
            disabled={busy || loading || undertimeCount === 0}
            title="Nastaví záporné „Z minulého“ na 0 (označené, jinak filtr)"
            onClick={() => runNormalize('undertime')}
          >
            Vyrovnat −
          </button>
        </div>
      </div>

      <h2 className="att-bank-print-title">{`Banka hodin — ${periodLabel}`}</h2>

      {loading ? <p className="attendance-muted att-bank-no-print">Načítání…</p> : null}

      <div className="att-cat__wrap att-cat__wrap--sticky att-bank-table-wrap">
        <table className="att-cat__table att-cat__table--leave att-bank-table">
          <thead>
            <tr>
              <th className="att-cat__col-narrow att-bank-no-print" aria-label="Výběr">
                <input
                  type="checkbox"
                  checked={allFilteredSelected}
                  disabled={busy || filtered.length === 0}
                  onChange={toggleAllFiltered}
                  title="Označit vše ve filtru"
                />
              </th>
              <th className="att-cat__col-name att-cat__col-name--person">Zaměstnanec</th>
              <th className="att-cat__col-narrow">Os. č.</th>
              <th className="att-cat__col-narrow">
                <button
                  type="button"
                  className="att-bank-sort-btn"
                  onClick={toggleHoursSort}
                  title="Řadit podle hodin"
                >
                  Z minulého
                  {hoursSort === 'hours_desc' ? ' ↓' : hoursSort === 'hours_asc' ? ' ↑' : ''}
                </button>
              </th>
              <th className="att-cat__col-narrow">Tento měsíc</th>
              <th className="att-cat__col-narrow">Aktuální</th>
              <th className="att-cat__col-narrow">Stav</th>
              <th
                className="att-cat__col-actions att-cat__col-fill att-bank-no-print"
                aria-label="Akce"
              />
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const carry = carryMinutes(r)
              const balClass =
                carry > 0
                  ? 'att-bank-bal--plus'
                  : carry < 0
                    ? 'att-bank-bal--minus'
                    : 'att-bank-bal--zero'
              return (
                <tr key={r.employee_id}>
                  <td className="att-cat__col-narrow att-bank-no-print">
                    <input
                      type="checkbox"
                      checked={selected.has(r.employee_id)}
                      disabled={busy}
                      onChange={() => toggleOne(r.employee_id)}
                    />
                  </td>
                  <td className="att-cat__col-name att-cat__col-name--person">
                    {bankRowDisplayName(r) || '—'}
                  </td>
                  <td className="att-cat__col-narrow">{r.personal_number || '—'}</td>
                  <td className={`att-cat__col-narrow ${balClass}`}>
                    <button
                      type="button"
                      className="att-leave-bank-link"
                      disabled={busy}
                      onClick={() => openBankDialog(r)}
                      title="Zůstatek banky — po Vyrovnat bude 0"
                    >
                      {r.opening_label}
                    </button>
                  </td>
                  <td className="att-cat__col-narrow">
                    {r.posted ? r.month_delta_label : '—'}
                  </td>
                  <td className={`att-cat__col-narrow ${balClass}`}>{r.current_balance_label}</td>
                  <td className="att-cat__col-narrow">{r.closed ? 'uzavřeno' : 'otevřeno'}</td>
                  <td className="att-cat__col-actions att-bank-no-print">
                    <button
                      type="button"
                      className="system-button att-cat__btn"
                      disabled={busy}
                      onClick={() => openBankDialog(r)}
                    >
                      Upravit
                    </button>
                  </td>
                </tr>
              )
            })}
            {!loading && filtered.length === 0 ? (
              <tr>
                <td colSpan={8} className="att-cat__empty">
                  {rows.length === 0 ? 'Žádná data.' : 'Žádný řádek neodpovídá filtru.'}
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <p className="att-abs-modal__hint att-bank-no-print">
        Vyrovnat + / − nastaví sloupec <strong>Z minulého</strong> na 0. Bez výběru řádků = celý
        aktuální filtr (hledání / jen+ / jen−).
      </p>

      {bankDialog ? (
        <div
          className="att-abs-modal-overlay att-bank-no-print"
          role="presentation"
          onClick={() => {
            if (!busy) setBankDialog(null)
          }}
        >
          <div
            className="att-abs-modal att-leave-bank-modal"
            role="dialog"
            aria-modal
            aria-labelledby="att-bank-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 id="att-bank-title">Banka hodin — {bankDialog.name}</h3>
            <p className="att-abs-modal__hint">
              Aktuální zůstatek: <strong>{bankDialog.balanceLabel}</strong> (
              {bankDialog.balanceMinutes} min)
            </p>
            <div className="att-abs-modal__fields">
              <label>
                Nový zůstatek (minuty)
                <input
                  type="number"
                  value={bankDialog.target}
                  disabled={busy || bankDialog.loading}
                  onChange={(e) => setBankDialog({ ...bankDialog, target: e.target.value })}
                />
              </label>
              <label>
                Poznámka
                <input
                  type="text"
                  value={bankDialog.note}
                  disabled={busy || bankDialog.loading}
                  placeholder="důvod úpravy"
                  onChange={(e) => setBankDialog({ ...bankDialog, note: e.target.value })}
                />
              </label>
            </div>
            <h4 className="att-leave-bank-modal__hist-title">Historie pohybů</h4>
            {bankDialog.loading ? (
              <p className="attendance-muted">Načítání…</p>
            ) : (
              <div className="att-leave-bank-modal__hist">
                <table className="att-cat__table att-leave-bank-modal__table">
                  <thead>
                    <tr>
                      <th>Kdy</th>
                      <th>Typ</th>
                      <th>Δ</th>
                      <th>Po</th>
                      <th>Pozn.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bankDialog.entries.map((e) => (
                      <tr key={e.id}>
                        <td>
                          {e.created_at ? String(e.created_at).slice(0, 16).replace('T', ' ') : '—'}
                        </td>
                        <td>{entryTypeLabel(e.entry_type)}</td>
                        <td>{e.delta_label}</td>
                        <td>{e.balance_after_label}</td>
                        <td title={e.note || undefined}>{e.note || '—'}</td>
                      </tr>
                    ))}
                    {bankDialog.entries.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="att-cat__empty">
                          Zatím žádné pohyby.
                        </td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              </div>
            )}
            <div className="att-abs-modal__actions">
              <button
                type="button"
                className="system-button"
                disabled={busy || bankDialog.loading || bankDialog.balanceMinutes === 0}
                onClick={() => saveBankDialog(true)}
                title="Nastavit zůstatek na 0"
              >
                Vyrovnat na 0
              </button>
              <button
                type="button"
                className="system-button"
                disabled={busy || bankDialog.loading}
                onClick={() => saveBankDialog(false)}
              >
                {busy ? 'Ukládám…' : 'Uložit zůstatek'}
              </button>
              <button
                type="button"
                className="system-button"
                style={{ background: '#64748b' }}
                disabled={busy}
                onClick={() => setBankDialog(null)}
              >
                Zavřít
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
